import * as XLSX from 'xlsx';
import type { CrmContactoImportFila } from '../types/crm';

export interface CrmContactosImportParseError {
  fila: number;
  mensaje: string;
}

export interface CrmContactosImportParseResult {
  filas: CrmContactoImportFila[];
  errores: CrmContactosImportParseError[];
}

type ColumnKey =
  | 'nombre'
  | 'empresa'
  | 'cargo'
  | 'telefono'
  | 'email'
  | 'origen'
  | 'tags'
  | 'web'
  | 'domicilio'
  | 'ciudad'
  | 'provincia'
  | 'pais'
  | 'atributo1'
  | 'atributo2'
  | 'atributo3';

const COLUMN_ALIASES: Record<ColumnKey, string[]> = {
  nombre: ['nombre', 'name', 'contacto', 'razon social', 'razonsocial'],
  empresa: ['empresa', 'company', 'compania', 'compañia', 'organizacion', 'organización'],
  cargo: ['cargo', 'puesto', 'titulo', 'título', 'position', 'role'],
  telefono: ['telefono', 'teléfono', 'phone', 'celular', 'movil', 'móvil', 'tel'],
  email: ['email', 'e-mail', 'correo', 'mail'],
  origen: ['origen', 'fuente', 'source', 'canal'],
  tags: ['tags', 'tag', 'etiquetas', 'etiqueta', 'labels'],
  web: ['web', 'sitio web', 'sitioweb', 'website', 'url', 'www'],
  domicilio: ['domicilio', 'direccion', 'dirección', 'address', 'calle'],
  ciudad: ['ciudad', 'localidad', 'city'],
  provincia: ['provincia', 'estado', 'state', 'region', 'región'],
  pais: ['pais', 'país', 'country', 'nation'],
  atributo1: ['atributo1', 'atributo 1'],
  atributo2: ['atributo2', 'atributo 2'],
  atributo3: ['atributo3', 'atributo 3'],
};

const TEMPLATE_ORDER: ColumnKey[] = [
  'nombre',
  'empresa',
  'cargo',
  'telefono',
  'email',
  'origen',
  'tags',
  'web',
  'domicilio',
  'ciudad',
  'provincia',
  'pais',
  'atributo1',
  'atributo2',
  'atributo3',
];

const stripAccents = (value: string): string =>
  value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const normalizeHeader = (value: unknown): string =>
  stripAccents(String(value ?? '').trim().toLowerCase());

const cellText = (value: unknown): string => {
  if (value == null) return '';
  return String(value).trim();
};

const resolveColumnIndices = (headers: string[]): Partial<Record<ColumnKey, number>> | null => {
  const result: Partial<Record<ColumnKey, number>> = {};
  (Object.keys(COLUMN_ALIASES) as ColumnKey[]).forEach((key) => {
    const aliases = COLUMN_ALIASES[key];
    const idx = headers.findIndex((h) => aliases.some((a) => h === a || h.includes(a)));
    if (idx >= 0) result[key] = idx;
  });
  return result.nombre != null ? result : null;
};

const rowToFila = (
  row: unknown[],
  columns: Partial<Record<ColumnKey, number>>,
  fila: number,
): CrmContactoImportFila | null => {
  const get = (key: ColumnKey): string => {
    const idx = columns[key];
    if (idx == null) return '';
    return cellText(row[idx]);
  };

  const nombre = get('nombre');
  const empresa = get('empresa');
  const cargo = get('cargo');
  const telefono = get('telefono');
  const email = get('email');
  const origen = get('origen');
  const tags = get('tags');
  const web = get('web');
  const domicilio = get('domicilio');
  const ciudad = get('ciudad');
  const provincia = get('provincia');
  const pais = get('pais');
  const atributo1 = get('atributo1');
  const atributo2 = get('atributo2');
  const atributo3 = get('atributo3');

  if (
    ![
      nombre,
      empresa,
      cargo,
      telefono,
      email,
      origen,
      tags,
      web,
      domicilio,
      ciudad,
      provincia,
      pais,
      atributo1,
      atributo2,
      atributo3,
    ].some((v) => v)
  ) {
    return null;
  }

  return {
    fila,
    nombre,
    empresa: empresa || null,
    cargo: cargo || null,
    telefono: telefono || null,
    email: email || null,
    origen: origen || null,
    tags: tags || null,
    web: web || null,
    domicilio: domicilio || null,
    ciudad: ciudad || null,
    provincia: provincia || null,
    pais: pais || null,
    atributo1: atributo1 || null,
    atributo2: atributo2 || null,
    atributo3: atributo3 || null,
  };
};

const parseMatrix = (rows: unknown[][]): CrmContactosImportParseResult => {
  if (!rows.length) {
    return { filas: [], errores: [{ fila: 0, mensaje: 'No hay datos para importar.' }] };
  }

  let headerRowIndex = -1;
  let columns: Partial<Record<ColumnKey, number>> | null = null;

  for (let r = 0; r < Math.min(rows.length, 10); r += 1) {
    const headers = (rows[r] as unknown[]).map(normalizeHeader);
    const resolved = resolveColumnIndices(headers);
    if (resolved) {
      headerRowIndex = r;
      columns = resolved;
      break;
    }
  }

  if (!columns) {
    columns = TEMPLATE_ORDER.reduce<Partial<Record<ColumnKey, number>>>((acc, key, idx) => {
      acc[key] = idx;
      return acc;
    }, {});
    headerRowIndex = -1;
  }

  const filas: CrmContactoImportFila[] = [];
  const errores: CrmContactosImportParseError[] = [];
  const start = headerRowIndex + 1;

  for (let i = start; i < rows.length; i += 1) {
    const row = rows[i] as unknown[];
    if (!row || row.every((c) => cellText(c) === '')) continue;
    const parsed = rowToFila(row, columns, i + 1);
    if (!parsed) continue;
    if (!parsed.nombre.trim()) {
      errores.push({ fila: i + 1, mensaje: 'El nombre es obligatorio.' });
      continue;
    }
    filas.push(parsed);
  }

  if (filas.length === 0 && errores.length === 0) {
    return {
      filas: [],
      errores: [
        {
          fila: 0,
          mensaje:
            'No se encontraron filas válidas. Verifique que exista la columna Nombre (o use la plantilla).',
        },
      ],
    };
  }

  return { filas, errores };
};

export const parseCrmContactosExcel = async (file: File): Promise<CrmContactosImportParseResult> => {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array', cellDates: false });
  if (!workbook.SheetNames.length) {
    return { filas: [], errores: [{ fila: 0, mensaje: 'El archivo no tiene hojas.' }] };
  }

  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    defval: '',
    raw: false,
  }) as unknown[][];

  return parseMatrix(rows);
};

export const parseCrmContactosClipboard = (text: string): CrmContactosImportParseResult => {
  const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
  if (!normalized) {
    return { filas: [], errores: [{ fila: 0, mensaje: 'El portapapeles está vacío.' }] };
  }

  const lines = normalized.split('\n').filter((l) => l.trim().length > 0);
  const delimiter = lines[0].includes('\t') ? '\t' : lines[0].includes(';') ? ';' : ',';
  const rows = lines.map((line) => line.split(delimiter));
  return parseMatrix(rows);
};

const INSTRUCCIONES_ROWS: Array<{
  Campo: string;
  Requerido: string;
  Tipo: string;
  'Largo max': string;
  Notas: string;
}> = [
  {
    Campo: 'Nombre',
    Requerido: 'Sí',
    Tipo: 'Texto',
    'Largo max': '200',
    Notas: 'Nombre del contacto',
  },
  {
    Campo: 'Empresa',
    Requerido: 'No',
    Tipo: 'Texto',
    'Largo max': '200',
    Notas: 'Si no existe, se crea; si existe, se actualizan los datos de empresa no vacíos',
  },
  {
    Campo: 'Cargo',
    Requerido: 'No',
    Tipo: 'Texto',
    'Largo max': '120',
    Notas: 'Cargo del contacto',
  },
  {
    Campo: 'Telefono',
    Requerido: 'No',
    Tipo: 'Texto',
    'Largo max': '50',
    Notas: 'Usado para detectar duplicados si no hay email',
  },
  {
    Campo: 'Email',
    Requerido: 'No',
    Tipo: 'Texto',
    'Largo max': '200',
    Notas: 'Clave principal de duplicados',
  },
  {
    Campo: 'Origen',
    Requerido: 'No',
    Tipo: 'Texto',
    'Largo max': '100',
    Notas: 'Debe coincidir con un origen del catálogo CRM',
  },
  {
    Campo: 'Tags',
    Requerido: 'No',
    Tipo: 'Texto',
    'Largo max': '—',
    Notas: 'Separados por coma o punto y coma; se crean si no existen',
  },
  {
    Campo: 'Web',
    Requerido: 'No',
    Tipo: 'Texto',
    'Largo max': '200',
    Notas: 'Dato de la empresa (Sitio web)',
  },
  {
    Campo: 'Domicilio',
    Requerido: 'No',
    Tipo: 'Texto',
    'Largo max': '300',
    Notas: 'Dato de la empresa (Dirección)',
  },
  {
    Campo: 'Ciudad',
    Requerido: 'No',
    Tipo: 'Texto',
    'Largo max': '100',
    Notas: 'Dato de la empresa',
  },
  {
    Campo: 'Provincia',
    Requerido: 'No',
    Tipo: 'Texto',
    'Largo max': '100',
    Notas: 'Dato de la empresa',
  },
  {
    Campo: 'Pais',
    Requerido: 'No',
    Tipo: 'Texto',
    'Largo max': '100',
    Notas: 'Dato de la empresa',
  },
  {
    Campo: 'Atributo1',
    Requerido: 'No',
    Tipo: 'Texto',
    'Largo max': '100',
    Notas: 'Atributo de empresa; el label se configura en Config CRM',
  },
  {
    Campo: 'Atributo2',
    Requerido: 'No',
    Tipo: 'Texto',
    'Largo max': '100',
    Notas: 'Atributo de empresa; el label se configura en Config CRM',
  },
  {
    Campo: 'Atributo3',
    Requerido: 'No',
    Tipo: 'Texto',
    'Largo max': '100',
    Notas: 'Atributo de empresa; el label se configura en Config CRM',
  },
];

export const downloadCrmContactosPlantilla = (): void => {
  const rows = [
    {
      Nombre: 'Juan Pérez',
      Empresa: 'Acme SA',
      Cargo: 'Gerente comercial',
      Telefono: '11-5555-1234',
      Email: 'juan.perez@acme.com',
      Origen: 'Web',
      Tags: 'vip,prospecto',
      Web: 'https://acme.com',
      Domicilio: 'Av. Corrientes 1234',
      Ciudad: 'CABA',
      Provincia: 'Buenos Aires',
      Pais: 'Argentina',
      Atributo1: '50',
      Atributo2: '',
      Atributo3: '',
    },
  ];
  const contactosSheet = XLSX.utils.json_to_sheet(rows);
  const instruccionesSheet = XLSX.utils.json_to_sheet(INSTRUCCIONES_ROWS);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, contactosSheet, 'Contactos');
  XLSX.utils.book_append_sheet(workbook, instruccionesSheet, 'Instrucciones');
  XLSX.writeFile(workbook, 'plantilla-contactos-crm.xlsx');
};
